import { useEffect, useState } from 'react'
import EmptyState from '../../reusable/EmptyState'
import { Button, ButtonDropdown, Icon, Popover, SpaceBetween } from '@amzn/awsui-components-react'
import { getAllocationStatus, getNotificationCountStatus } from '../../reusable/AllocationUtils'
import { ColDef, GridApi } from 'ag-grid-community'
import LinkButton from '../../reusable/LinkButton'
import { addGeneralFilterParams, dashFallbackRenderer } from '../../grid/Utils'
import GenericAGGridTable from '../../grid/GenericAGGridTable'
import { FILTER_TYPE } from '../../grid/Constants'
import { convertToLocalTime } from '../../../common/Util'

const StatusTeamTable = (props) => {
    const {
        selectEndDate,
        teams,
        selectedTeams,
        onSelectedTeamsChange,
        isLoading,
        isOrgLoading,
        onClickSendNotification,
        isTeamButtonsLoading,
    } = props

    const [gridApi, setGridApi] = useState<GridApi>()
    const columnDefs: ColDef[] = [
        { field: 'team_id', headerName: 'Team ID', hide: true },
        {
            field: 'team_name',
            headerName: 'Team',
            checkboxSelection: true,
            headerCheckboxSelection: true,
            sort: 'asc',
        },
        { field: 'org_name', headerName: 'Organization' },
        { field: 'group_name', headerName: 'Group' },
        {
            field: 'allocation_page',
            headerComponent: () => (
                <SpaceBetween size='xs' direction='horizontal' alignItems='center'>
                    Allocation Page
                    <Popover
                        dismissButton={false}
                        position='top'
                        triggerType='custom'
                        content='Redirect to the allocation page.'
                    >
                        <Icon name='status-info' variant='subtle' />
                    </Popover>
                </SpaceBetween>
            ),
            cellRenderer: ({ data }) => (
                <LinkButton
                    path={`/allocation/${data.team_id}/${selectEndDate}`}
                    variant='link'
                    iconName='user-profile'
                    target='_blank'
                />
            ),
        },
        {
            field: 'allocation_browser_page',
            headerComponent: () => (
                <SpaceBetween size='xs' direction='horizontal' alignItems='center'>
                    Browser Page
                    <Popover
                        dismissButton={false}
                        position='top'
                        size='small'
                        triggerType='custom'
                        content='Redirect to the allocation browser page.'
                    >
                        <Icon name='status-info' variant='subtle' />
                    </Popover>
                </SpaceBetween>
            ),
            cellRenderer: ({ data }) => (
                <LinkButton
                    path={`/history/team/${data.team_id}`}
                    variant='link'
                    iconName='calendar'
                    target='_blank'
                />
            ),
        },
        {
            field: 'allocations_headcount',
            headerName: 'Allocations / Headcount',
            valueGetter: ({ data }) => `${data.total_allocation} / ${data.total_headcount}`,
        },
        {
            field: 'status',
            headerName: 'Status',
            cellRenderer: ({ value }) => getAllocationStatus(value),
        },
        {
            field: 'notification_count',
            headerName: 'Notification Count',
            cellRenderer: ({ value }) => getNotificationCountStatus(value),
            filter: FILTER_TYPE.NUMBER,
        },
        { field: 'primary_alias', headerName: 'Primary Contact' },
        {
            field: 'registered_users',
            headerName: 'Registered Users',
            valueGetter: ({ data }) => (data.registered_users ?? []).toString(),
        },
    ]
    columnDefs.forEach((col) => {
        addGeneralFilterParams(col)
        col.tooltipField = col.field
        col.cellRenderer ??= dashFallbackRenderer
    })

    useEffect(() => {
        if (isOrgLoading || isLoading) {
            gridApi?.showLoadingOverlay()
        } else {
            gridApi?.setGridOption('rowData', teams)
        }
    }, [gridApi, teams, isLoading, isOrgLoading])

    return (
        <GenericAGGridTable
            setGridApi={setGridApi}
            colDefs={columnDefs}
            rowSelectionType='multiple'
            onSelectionChanged={(event) => {
                onSelectedTeamsChange(event.api.getSelectedRows())
            }}
            noRowsOverlayComponent={() => (
                <>
                    <br /> <br /> <br />
                    <EmptyState
                        title='No Teams'
                        subtitle="No Teams to display. Select organization(s) in the above table and click 'Teams Preview' button for team(s) preview."
                        action={<></>}
                    />
                </>
            )}
            includeTableHeader={true}
            primaryButtonPermission={false}
            primaryButton={<></>}
            actionDropdownPermission={true}
            actionDropdown={
                <ButtonDropdown
                    loading={isTeamButtonsLoading()}
                    onItemClick={(e) => onClickSendNotification(e.detail.id)}
                    items={[
                        {
                            text: 'Selected Teams',
                            id: 'selected_teams',
                            disabled: !selectedTeams.length,
                        },
                        { text: 'All Incomplete Teams', id: 'all_incomplete_teams' },
                        { text: 'All Teams', id: 'all_teams' },
                    ]}
                >
                    Send Notification
                </ButtonDropdown>
            }
            exportButton={
                <Button
                    disabled={isOrgLoading || isLoading}
                    onClick={() => {
                        gridApi?.exportDataAsExcel({
                            fileName: `FalconDashboardTeams_${convertToLocalTime(new Date())}.xlsx`,
                        })
                    }}
                >
                    Export
                </Button>
            }
        />
    )
}

export default StatusTeamTable
